import { el, clear, toast } from '../core/dom.js';
import { activeHabits, getState, planFor } from '../core/state.js';
import { consumptionMethods, events, metaSet, moneyModels, purchaseTemplates, rescueStats } from '../storage/db.js';
import { formatDuration } from '../core/time.js';
import { buildDailyBuckets, buildTriggerMap } from '../progress/insights.js';
import { buildInsightCards, humanEventLabel } from '../progress/insight-cards.js';
import { renderInsightCard } from '../components/insight-card.js';
import { suggestPlanAdjustment } from '../plans/adaptation.js';
import { buildResilienceStreaks } from '../progress/streaks.js';
import { buildWeeklySummary } from '../progress/summary.js';
import { habitDisplayLabel } from '../core/display.js';
import { settings as settingsStore } from '../storage/db.js';
import { costPerUse, fmtMoney, spendingCapStatus, totalSubscriptions } from '../progress/money.js';
import { resolvePurchases } from '../progress/purchases.js';
import { getModule } from '../rescue/rescue-registry.js';
import { computeSavingsSummary } from '../progress/savings.js';
import { normalizeUsageEvents } from '../rules/usage-normalizer.js';
import { currentRoute, navigate } from '../router.js';

export async function renderProgress(parent: HTMLElement) {
  clear(parent);
  const st = getState();
  const habitsList = activeHabits(st);
  if (habitsList.length === 0) {
    parent.appendChild(el('p', {}, 'No active plan.'));
    return;
  }
  const requested = currentRoute().params.get('habit');
  const habit = habitsList.find((h) => h.id === requested) ?? st.activeHabit ?? habitsList[0];
  if (!habit) {
    parent.appendChild(el('p', {}, 'No active plan.'));
    return;
  }
  const { plan: activePlan, planState: activePlanState } = planFor(habit.id, st);
  if (!activePlan || !activePlanState) {
    parent.appendChild(el('p', {}, 'Plan missing.'));
    return;
  }

  // Habit switcher (only show if multiple)
  if (habitsList.length > 1) {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Plans'),
      el('div', { class: 'kbd-list' },
        ...habitsList.map((h) => {
          const isActive = h.id === habit.id;
          const t = el('button', {
            class: `tag${isActive ? ' active' : ''}`,
            attrs: { type: 'button', ...(isActive ? { 'aria-current': 'true' } : {}) },
            onclick: () => navigate('/progress', { habit: h.id }),
          }, habitDisplayLabel(h));
          return t;
        }),
      ),
    ));
  }

  const habitId = habit.id;
  const all = await events.byHabit(habitId);
  const usages = all.filter((e) => e.type === 'usage_logged');
  const successes = all.filter((e) => e.type === 'success_logged');
  const rescuesStarted = all.filter((e) => e.type === 'rescue_started').length;
  const rescuesCompleted = all.filter((e) => e.type === 'rescue_completed').length;
  const urges = all.filter((e) => e.type === 'urge_logged').length;

  // Time reclaimed (interval): sum of waitMs over baseline
  const ps = activePlanState;
  let timeReclaimedMs = 0;
  if (ps.state.planType === 'interval') {
    const baseline = ps.state.baselineIntervalMs;
    for (const u of usages) {
      const wait = (u.payload['waitMs'] as number | null) ?? 0;
      if (wait > 0) timeReclaimedMs += Math.max(0, wait - baseline);
    }
  }

  const money = await moneyModels.get(habitId);
  let moneySaved = 0;
  if (money?.enabled) {
    let baselineUsesPerWeek: number | undefined;
    if (ps.state.planType === 'interval') {
      const usesPerDay = (16 * 60 * 60_000) / ps.state.baselineIntervalMs;
      baselineUsesPerWeek = usesPerDay * 7;
    } else if (ps.state.planType === 'quota') {
      const period = ps.state.period;
      baselineUsesPerWeek = period === 'daily' ? ps.state.baselineQuota * 7
        : period === 'weekly' ? ps.state.baselineQuota
        : ps.state.baselineQuota / 4.33;
    }
    const cpu = costPerUse(money, { baselineUsesPerWeek });
    if (cpu > 0) {
      if (ps.state.planType === 'interval') {
        const first = usages[0];
        if (first) {
          const elapsedMs = Date.now() - new Date(first.occurredAt).getTime();
          const baselineRate = ps.state.baselineIntervalMs;
          const expected = Math.floor(elapsedMs / baselineRate);
          const avoided = Math.max(0, expected - usages.length);
          moneySaved = avoided * cpu;
        }
      } else if (ps.state.planType === 'quota') {
        const periods = ps.state.successPeriods;
        const reduced = ps.state.baselineQuota - ps.state.currentQuota;
        moneySaved = Math.max(0, periods * reduced) * cpu;
      }
    }
  }

  // Lead with insight cards. Old raw counts are still available below.
  const now = new Date();
  const weekStart = new Date(now); weekStart.setDate(weekStart.getDate() - 7);
  const priorWeekStart = new Date(now); priorWeekStart.setDate(priorWeekStart.getDate() - 14);
  const cards = buildInsightCards({
    events: all,
    weekStartIso: weekStart.toISOString(),
    weekEndIso: now.toISOString(),
    priorWeekStartIso: priorWeekStart.toISOString(),
    priorWeekEndIso: weekStart.toISOString(),
    unit: habit.unitSingular,
    ...(habit.domain ? { domain: habit.domain } : {}),
  });
  // Trigger-cluster cards get an Apply action that biases rescue picks
  // toward the appropriate kinds by adding the trigger to settings.
  // Rescue-effectiveness cards offer a Favorite action that stars the
  // module so the engine surfaces it first.
  for (const c of cards) {
    if (c.id.startsWith('pattern_trigger_') && !c.primary) {
      const trigger = c.id.replace('pattern_trigger_', '');
      c.primary = {
        label: 'Bias my rescues',
        onClick: async () => {
          const cur = await settingsStore.get();
          const triggers = new Set(cur.triggers ?? []);
          triggers.add(trigger);
          await settingsStore.set({ ...cur, triggers: [...triggers] });
          toast(`Rescue picks now bias toward ${trigger.replace(/_/g, ' ')} support.`);
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      };
    } else if (c.id.startsWith('pattern_rescue_') && !c.primary) {
      const moduleId = c.id.replace('pattern_rescue_', '');
      c.primary = {
        label: 'Favorite this module',
        onClick: async () => {
          const cur = await settingsStore.get();
          const favorites = new Set(cur.favoriteRescueModuleIds ?? []);
          if (favorites.has(moduleId)) {
            favorites.delete(moduleId);
            toast('Removed from favorites.');
          } else {
            favorites.add(moduleId);
            toast('Added to favorites — it will surface first.');
          }
          await settingsStore.set({ ...cur, favoriteRescueModuleIds: [...favorites] });
          window.dispatchEvent(new CustomEvent('wean:state-changed'));
        },
      };
    }
    parent.appendChild(renderInsightCard(c));
  }

  const confidence = st.meta.onboarding_metadata?.confidence;
  const suggestion = suggestPlanAdjustment({ plan: activePlan, planState: activePlanState, events: all, ...(confidence !== undefined ? { confidence } : {}) });
  const snoozeMap = st.meta.suggestion_snoozed_until ?? {};
  const snoozedUntil = suggestion ? snoozeMap[suggestion.id] : undefined;
  const isSnoozed = !!snoozedUntil && new Date(snoozedUntil).getTime() > Date.now();
  if (suggestion && suggestion.kind !== 'hold_steady' && !isSnoozed) {
    const card = el('section', {
      class: `card insight-card insight-card--${suggestion.kind}`,
      attrs: { 'aria-label': suggestion.headline, style: 'border-color:var(--accent-soft);' },
    });
    card.appendChild(el('div', { class: 'eyebrow' }, 'Plan suggestion'));
    card.appendChild(el('h2', { attrs: { style: 'margin:0 0 0.25rem;font-size:1.05rem;' } }, suggestion.headline));
    card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin:0;font-size:0.9rem;' } }, suggestion.evidence));
    if (suggestion.apply) {
      card.appendChild(el('div', { class: 'row', attrs: { style: 'flex-wrap:wrap;gap:0.5rem;margin-top:0.5rem;' } },
        el('button', {
          class: 'btn primary small', attrs: { type: 'button' },
          onclick: async () => {
            await suggestion.apply!();
            toast('Target updated.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'Apply'),
        el('button', {
          class: 'btn ghost small', attrs: { type: 'button' },
          onclick: async () => {
            const until = new Date(Date.now() + 7 * 86_400_000).toISOString();
            const cur = getState().meta.suggestion_snoozed_until ?? {};
            await metaSet('suggestion_snoozed_until', { ...cur, [suggestion.id]: until });
            toast('Snoozed for a week.');
            window.dispatchEvent(new CustomEvent('wean:state-changed'));
          },
        }, 'Not now'),
      ));
    }
    parent.appendChild(card);
  }

  parent.appendChild(el('a', { href: `#/reflect?habit=${habit.id}`, class: 'btn block', attrs: { style: 'margin: 0.75rem 0;' } }, 'Add weekly reflection'));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Overview'),
    el('div', { class: 'row' },
      kpi('Logged uses', String(usages.length)),
      kpi('Successful waits', String(successes.length)),
      kpi('Urges logged', String(urges)),
    ),
    el('div', { class: 'row', attrs: { style: 'margin-top:0.75rem;' } },
      kpi('Rescue starts', String(rescuesStarted)),
      kpi('Rescue completes', String(rescuesCompleted)),
      kpi('Money saved (est.)', money?.enabled ? fmtMoney(moneySaved, money.currency) : '—'),
    ),
  ));

  // Resilience streaks — multiple shame-safe metrics so a single rough day
  // doesn't erase visible progress.
  const streaks = buildResilienceStreaks(all);
  if (streaks.some((s) => s.current > 0 || s.longest > 0)) {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Resilience streaks'),
      el('div', { class: 'row', attrs: { style: 'flex-wrap:wrap;gap:0.6rem;' } },
        ...streaks.map((s) => el('div', { class: 'kpi', attrs: { 'aria-label': `${s.label}: ${s.current} current, ${s.longest} longest.` } },
          el('div', { class: 'label' }, s.label),
          el('div', { class: 'value' }, String(s.current)),
          el('div', { class: 'muted', attrs: { style: 'font-size:0.7rem;' } }, `longest ${s.longest}`),
        )),
      ),
      el('p', { class: 'muted', attrs: { style: 'margin:0.5rem 0 0;font-size:0.8rem;' } },
        'A rough day does not reset every streak.'),
    ));
  }

  if (ps.state.planType === 'interval') {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Time reclaimed'),
      el('div', { class: 'big' }, formatDuration(timeReclaimedMs)),
      el('div', { class: 'muted' }, 'Extra waiting beyond your baseline gap.'),
    ));
  }

  // Phase 2 method substitution: compare last 7d vs prior 7d per method.
  const methodsForInsights = await consumptionMethods.byHabit(habitId);
  if (methodsForInsights.length > 0) {
    const sevenDays = 7 * 86_400_000;
    const now = Date.now();
    const lastWeekStart = new Date(now - sevenDays).toISOString();
    const priorWeekStart = new Date(now - 2 * sevenDays).toISOString();
    const usageEvents = all.filter((e) => e.type === 'usage_logged');
    const lastWeekUsage = normalizeUsageEvents(usageEvents.filter((e) => e.occurredAt >= lastWeekStart), methodsForInsights);
    const priorWeekUsage = normalizeUsageEvents(usageEvents.filter((e) => e.occurredAt >= priorWeekStart && e.occurredAt < lastWeekStart), methodsForInsights);
    const totalsByKind = (entries: typeof lastWeekUsage): Map<string, number> => {
      const map = new Map<string, number>();
      for (const u of entries) {
        if (!u.methodKind) continue;
        map.set(u.methodKind, (map.get(u.methodKind) ?? 0) + u.domainUnits);
      }
      return map;
    };
    const last = totalsByKind(lastWeekUsage);
    const prior = totalsByKind(priorWeekUsage);
    const allKinds = new Set<string>([...last.keys(), ...prior.keys()]);
    const totalLast = lastWeekUsage.reduce((acc, u) => acc + u.domainUnits, 0);
    const totalPrior = priorWeekUsage.reduce((acc, u) => acc + u.domainUnits, 0);
    if (allKinds.size > 0 && (totalLast > 0 || totalPrior > 0)) {
      const rows = [...allKinds].map((kind) => {
        const lastN = last.get(kind) ?? 0;
        const priorN = prior.get(kind) ?? 0;
        const delta = lastN - priorN;
        const pct = priorN > 0 ? Math.round((delta / priorN) * 100) : (lastN > 0 ? 100 : 0);
        const arrow = delta < 0 ? '↓' : delta > 0 ? '↑' : '→';
        const label = methodsForInsights.find((m) => m.kind === kind)?.label ?? kind;
        return el('div', { class: 'list-row' },
          el('div', { class: 'label' }, label),
          el('div', { class: 'right' }, `${priorN} → ${lastN}  ${arrow} ${Math.abs(pct)}%`),
        );
      });
      const totalDelta = totalLast - totalPrior;
      const totalPct = totalPrior > 0 ? Math.round((totalDelta / totalPrior) * 100) : (totalLast > 0 ? 100 : 0);
      const totalArrow = totalDelta < 0 ? '↓' : totalDelta > 0 ? '↑' : '→';
      parent.appendChild(el('div', { class: 'card' },
        el('h2', {}, 'Method substitution · last 7 vs prior 7 days'),
        ...rows,
        el('div', { class: 'list-row', attrs: { style: 'font-weight:600; border-top:1px solid var(--border-strong); margin-top:6px;' } },
          el('div', { class: 'label' }, `Total ${habit.domain ?? habit.category}`),
          el('div', { class: 'right' }, `${totalPrior} → ${totalLast}  ${totalArrow} ${Math.abs(totalPct)}%`),
        ),
      ));
    }
  }

  // Subscriptions card — active monthly + canceled monthly protected.
  const allTemplates = await purchaseTemplates.byHabit(habitId);
  const subTotals = totalSubscriptions(allTemplates, money?.currency ?? 'USD');
  if (subTotals.activeMonthlyCost > 0 || subTotals.canceledMonthlyProtected > 0) {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Subscriptions'),
      el('div', { class: 'row' },
        kpi('Active /month', fmtMoney(subTotals.activeMonthlyCost, subTotals.currency)),
        kpi('Canceled /month', fmtMoney(subTotals.canceledMonthlyProtected, subTotals.currency)),
      ),
    ));
  }

  // Spending cap status — gambling/shopping/gaming guardrail.
  const capStatus = spendingCapStatus(money, all);
  if (capStatus) {
    const border = capStatus.state === 'over' ? 'var(--danger)'
      : capStatus.state === 'warn' ? 'var(--warn)' : 'var(--accent-soft)';
    parent.appendChild(el('div', { class: 'card', attrs: { style: `border-color:${border};` } },
      el('h2', {}, `Spending cap (${capStatus.period})`),
      el('div', { class: 'row' },
        kpi('Used', fmtMoney(capStatus.used, capStatus.currency)),
        kpi('Cap', fmtMoney(capStatus.cap, capStatus.currency)),
        kpi('Remaining', fmtMoney(capStatus.remaining, capStatus.currency)),
      ),
      el('p', { class: 'muted', attrs: { style: 'margin:0.4rem 0 0;font-size:0.85rem;' } },
        capStatus.state === 'over'
          ? 'Over cap this period.'
          : capStatus.state === 'warn'
            ? `Past ${capStatus.warnAtPct}% of cap — slow down.`
            : 'Inside cap.'),
    ));
  }

  // Phase 2 savings card: only render when this habit has purchase templates.
  const templates = allTemplates.filter((t) => t.active);
  if (templates.length > 0) {
    const methods = await consumptionMethods.byHabit(habitId);
    const periodEnd = new Date();
    const periodStart = new Date(periodEnd.getTime() - 30 * 86_400_000);
    const periodStartIso = periodStart.toISOString();
    const periodEndIso = periodEnd.toISOString();

    const purchases = resolvePurchases(all).map((p) => ({ ...p, habitId }));
    const usageInRange = all.filter((e) =>
      e.type === 'usage_logged'
      && e.occurredAt >= periodStartIso
      && e.occurredAt <= periodEndIso,
    );
    const normalized = normalizeUsageEvents(usageInRange, methods);
    const currency = templates[0]?.currency ?? 'USD';

    const summary = computeSavingsSummary({
      habitId, currency,
      periodStart: periodStartIso, periodEnd: periodEndIso,
      templates, methods, purchases, usage: normalized,
    });

    const card = el('div', { class: 'card' },
      el('h2', {}, 'Estimated savings · last 30 days'),
      el('div', { class: 'row' },
        kpi('Actual spend', fmtMoney(summary.actualSpend, summary.currency)),
        kpi('Estimated saved', fmtMoney(summary.estimatedSavings, summary.currency)),
      ),
      el('div', { class: 'row', attrs: { style: 'margin-top:0.75rem;' } },
        kpi('Old-pace spend', fmtMoney(summary.expectedBaselineSpend, summary.currency)),
        kpi('Above old pace', fmtMoney(summary.spendAboveBaseline, summary.currency)),
      ),
      el('div', { class: 'muted', attrs: { style: 'margin-top:0.5rem; font-size:0.8rem;' } },
        `Confidence: ${summary.confidence}. ${summary.explanation}`),
    );
    if (summary.breakdown.length > 0) {
      card.appendChild(el('div', { attrs: { style: 'margin-top: var(--space-3);' } },
        ...summary.breakdown.map((b) =>
          el('div', { class: 'list-row' },
            el('div', {},
              el('div', { class: 'label' }, b.label),
              el('div', { class: 'sub' }, b.mode.replace(/_/g, ' ')),
            ),
            el('div', { class: 'right' }, b.estimatedSavings > 0
              ? `saved ${fmtMoney(b.estimatedSavings, summary.currency)}`
              : b.spendAboveBaseline > 0
                ? `+${fmtMoney(b.spendAboveBaseline, summary.currency)} above pace`
                : `spent ${fmtMoney(b.actualSpend, summary.currency)}`),
          )
        ),
      ));
    }
    parent.appendChild(card);
  }

  // Weekly trend
  const buckets = buildDailyBuckets(all, 7);
  const max = Math.max(1, ...buckets.map((b) => Math.max(b.uses, b.successes, b.rescues, b.urges)));
  const totalUses = buckets.reduce((acc, b) => acc + b.uses, 0);
  const totalSuccesses = buckets.reduce((acc, b) => acc + b.successes, 0);
  const totalRescues = buckets.reduce((acc, b) => acc + b.rescues, 0);
  const chartSummary = `Last 7 days: ${totalUses} uses, ${totalSuccesses} successes, ${totalRescues} rescues.`;
  parent.appendChild(el('div', { class: 'card' },
    el('h2', { attrs: { id: 'last-7-days-heading' } }, 'Last 7 days'),
    el('div', { class: 'visually-hidden' }, chartSummary),
    el('div', {
      attrs: {
        style: 'display:grid; grid-template-columns:repeat(7, 1fr); gap:6px; align-items:end; height:120px;',
        role: 'img',
        'aria-labelledby': 'last-7-days-heading',
        'aria-describedby': 'last-7-days-summary',
      },
    },
      ...buckets.map((b) => {
        const total = b.uses + b.successes + b.rescues;
        const heightPct = (total / max) * 100;
        const usesPct = total === 0 ? 0 : (b.uses / total) * heightPct;
        const okPct = total === 0 ? 0 : (b.successes / total) * heightPct;
        const resPct = total === 0 ? 0 : (b.rescues / total) * heightPct;
        const wrap = el('div', {
          attrs: {
            style: 'display:flex; flex-direction:column; gap:2px; justify-content:flex-end; height:100%;',
            'aria-label': `${b.localDate}: ${b.uses} uses, ${b.successes} successes, ${b.rescues} rescues.`,
          },
        });
        wrap.appendChild(el('div', { attrs: { style: `height:${resPct}%; background:var(--accent); border-radius:3px;` } }));
        wrap.appendChild(el('div', { attrs: { style: `height:${okPct}%; background:var(--ok); border-radius:3px;` } }));
        wrap.appendChild(el('div', { attrs: { style: `height:${usesPct}%; background:var(--data-neutral); border-radius:3px;` } }));
        wrap.appendChild(el('div', { attrs: { style: 'font-size:0.65rem; color:var(--text-muted); text-align:center;' } }, b.localDate.slice(5)));
        return wrap;
      }),
    ),
    el('div', {
      class: 'muted',
      attrs: { id: 'last-7-days-summary', style: 'font-size:0.75rem; margin-top:0.5rem;' },
    }, `${chartSummary} Grey: uses · Green: successes · Teal: rescues.`),
  ));

  // Trigger map (settings.triggers + urge events with payload triggers)
  const allTriggers = [...(st.settings.triggers ?? [])];
  for (const e of all) {
    if (e.type === 'urge_logged' && Array.isArray(e.payload?.['triggers'])) {
      for (const t of e.payload['triggers'] as string[]) allTriggers.push(t);
    }
  }
  const triggerMap = buildTriggerMap(allTriggers);
  if (triggerMap.length > 0) {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Trigger map'),
      el('div', { class: 'kbd-list' },
        ...triggerMap.slice(0, 10).map((t) => el('span', { class: 'tag' }, `${t.tag.replace(/_/g, ' ')} · ${t.count}`)),
      ),
    ));
  }

  // Rescue effectiveness
  const stats = await rescueStats.byHabit(habitId);
  if (stats.length > 0) {
    parent.appendChild(el('div', { class: 'card' },
      el('h2', {}, 'Rescue effectiveness'),
      ...stats.map((s) => {
        const rate = s.starts > 0 ? Math.round((s.completions / s.starts) * 100) : 0;
        const niceName = getModule(s.moduleId)?.name ?? s.moduleId;
        return el('div', { attrs: { style: 'display:flex; justify-content:space-between; padding:4px 0;' } },
          el('span', {}, niceName),
          el('span', { class: 'muted' }, `${s.completions}/${s.starts} (${rate}%)`),
        );
      }),
    ));
  }

  parent.appendChild(buildAccountabilityCard(habit, all));
  parent.appendChild(buildActivityLog(all));
}

function buildAccountabilityCard(habit: import('../core/types.js').HabitRecord, events: import('../core/types.js').AppEvent[]): HTMLElement {
  let redact = false;
  const card = el('details', { class: 'card', attrs: { style: 'padding:0.75rem 1rem;' } });
  card.appendChild(el('summary', { attrs: { style: 'cursor:pointer;font-weight:600;' } }, 'Share weekly summary'));
  const preview = el('pre', {
    attrs: { style: 'white-space:pre-wrap;font-family:var(--font-mono);font-size:0.8rem;margin-top:0.5rem;padding:0.5rem;background:var(--surface-2);border-radius:var(--radius-2);max-height:280px;overflow:auto;' },
  });
  const refresh = (): void => {
    preview.textContent = buildWeeklySummary({ habit, events, redact });
  };
  refresh();
  card.appendChild(preview);
  const controls = el('div', { class: 'row', attrs: { style: 'gap:0.5rem;flex-wrap:wrap;margin-top:0.5rem;' } });
  const redactBtn = el('button', { class: 'btn small ghost', attrs: { type: 'button' } }, 'Hide habit name');
  redactBtn.onclick = () => {
    redact = !redact;
    redactBtn.textContent = redact ? 'Show habit name' : 'Hide habit name';
    refresh();
  };
  controls.appendChild(redactBtn);
  controls.appendChild(el('button', {
    class: 'btn small primary', attrs: { type: 'button' },
    onclick: async () => {
      try {
        await navigator.clipboard.writeText(preview.textContent ?? '');
        toast('Summary copied. Review before sharing.');
      } catch {
        toast('Copy failed. Select the text and copy manually.');
      }
    },
  }, 'Copy to clipboard'));
  card.appendChild(controls);
  card.appendChild(el('p', { class: 'muted', attrs: { style: 'margin:0.5rem 0 0;font-size:0.78rem;' } },
    'Generated locally. Nothing leaves this device unless you paste it somewhere.'));
  return card;
}

interface ActivityFilterState { range: '7d' | '30d' | '90d' | 'all'; kind: ActivityKind }
type ActivityKind = 'all' | 'use' | 'wait' | 'urge' | 'rescue' | 'purchase';

function buildActivityLog(events: import('../core/types.js').AppEvent[]): HTMLElement {
  const state: ActivityFilterState = { range: '7d', kind: 'all' };
  const card = el('details', { class: 'card', attrs: { style: 'padding:0.75rem 1rem;' } });
  card.appendChild(el('summary', { attrs: { style: 'cursor:pointer;font-weight:600;' } }, 'Recent activity'));
  const controls = el('div', { class: 'row', attrs: { style: 'gap:0.5rem;flex-wrap:wrap;margin-top:0.5rem;' } });
  const list = el('div', { attrs: { style: 'margin-top:0.5rem;' } });
  card.appendChild(controls);
  card.appendChild(list);

  const rangeOptions: ActivityFilterState['range'][] = ['7d', '30d', '90d', 'all'];
  for (const r of rangeOptions) {
    const isActive = state.range === r;
    const btn = el('button', {
      class: `tag${isActive ? ' active' : ''}`,
      dataset: { filter: 'range', value: r },
      attrs: { type: 'button', 'aria-pressed': isActive ? 'true' : 'false' },
    }, r === 'all' ? 'All' : r);
    btn.onclick = () => { state.range = r; refresh(); };
    controls.appendChild(btn);
  }
  controls.appendChild(el('span', { class: 'muted', attrs: { style: 'opacity:0.5;padding:0 0.25rem;' } }, '·'));
  const kindOptions: ActivityKind[] = ['all', 'use', 'wait', 'urge', 'rescue', 'purchase'];
  const kindLabels: Record<ActivityKind, string> = {
    all: 'All', use: 'Use', wait: 'Wait', urge: 'Urge', rescue: 'Rescue', purchase: 'Purchase',
  };
  for (const k of kindOptions) {
    const isActive = state.kind === k;
    const btn = el('button', {
      class: `tag${isActive ? ' active' : ''}`,
      dataset: { filter: 'kind', value: k },
      attrs: { type: 'button', 'aria-pressed': isActive ? 'true' : 'false' },
    }, kindLabels[k]);
    btn.onclick = () => { state.kind = k; refresh(); };
    controls.appendChild(btn);
  }

  function refresh(): void {
    while (list.firstChild) list.removeChild(list.firstChild);
    for (const child of [...controls.children]) {
      const elBtn = child as HTMLElement;
      const kind = elBtn.dataset['filter'];
      const value = elBtn.dataset['value'];
      if (!kind || !value) continue;
      const isActive = (kind === 'range' && value === state.range)
        || (kind === 'kind' && value === state.kind);
      elBtn.classList.toggle('active', isActive);
      elBtn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    }
    const filtered = filterActivity(events, state);
    if (filtered.length === 0) {
      list.appendChild(el('p', { class: 'muted', attrs: { style: 'font-size:0.85rem;' } }, 'No matching activity.'));
      return;
    }
    for (const e of filtered.slice(0, 50)) {
      list.appendChild(el('div', {
        attrs: { style: 'display:flex; justify-content:space-between; padding:4px 0; font-size:0.9rem;' },
      },
        el('span', {}, humanEventLabel(e.type, e.payload)),
        el('span', { class: 'muted' }, new Date(e.occurredAt).toLocaleString()),
      ));
    }
    if (filtered.length > 50) {
      list.appendChild(el('p', { class: 'muted', attrs: { style: 'font-size:0.8rem;margin-top:0.5rem;' } },
        `Showing 50 of ${filtered.length}. Narrow the range or kind to see fewer.`));
    }
  }
  refresh();
  return card;
}

function filterActivity(
  events: import('../core/types.js').AppEvent[],
  state: ActivityFilterState,
): import('../core/types.js').AppEvent[] {
  const cutoff = state.range === 'all' ? 0
    : state.range === '7d' ? Date.now() - 7 * 86_400_000
    : state.range === '30d' ? Date.now() - 30 * 86_400_000
    : Date.now() - 90 * 86_400_000;
  return events
    .filter((e) => e.status !== 'undone')
    .filter((e) => new Date(e.occurredAt).getTime() >= cutoff)
    .filter((e) => matchKind(e, state.kind))
    .slice()
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
}

function matchKind(e: import('../core/types.js').AppEvent, kind: ActivityKind): boolean {
  if (kind === 'all') return true;
  if (kind === 'use') return e.type === 'usage_logged' || e.type === 'usage_session_ended';
  if (kind === 'wait') {
    const sub = (e.payload as { kind?: string }).kind;
    return e.type === 'success_logged' && (sub === 'waited' || sub === 'urge_passed' || sub === 'stayed_in_window' || sub === 'used_less');
  }
  if (kind === 'urge') {
    return e.type === 'urge_logged' || e.type === 'urge_episode_started' || e.type === 'urge_episode_closed';
  }
  if (kind === 'rescue') {
    return e.type === 'rescue_started' || e.type === 'rescue_completed' || e.type === 'rescue_abandoned';
  }
  if (kind === 'purchase') {
    if (e.type === 'purchase_logged' || e.type === 'purchase_corrected' || e.type === 'purchase_deleted') return true;
    const sub = (e.payload as { kind?: string }).kind;
    return e.type === 'success_logged' && sub === 'skipped_purchase';
  }
  return false;
}

function kpi(label: string, value: string): HTMLElement {
  return el('div', { class: 'kpi' },
    el('div', { class: 'label' }, label),
    el('div', { class: 'value' }, value),
  );
}
